import { keyboard } from '@helpers/constants';
import { useDisableScroll } from '@helpers/hooks/use-disable-scroll/useDisableScroll';
import clsx from 'clsx';
import React, {
  ForwardRefExoticComponent,
  Fragment,
  forwardRef,
  ReactNode,
  RefAttributes,
  useEffect,
  useRef,
} from 'react';

import { Button, ButtonProps } from '../button/Button';
import { Portal } from '../portal';
import styles from './Modal.module.scss';

export const CLOSE_MODAL_ID = 'modal-close-button';
export const MODAL_ID = 'modal';
export const MODAL_TITLE_ID = 'modal-title';

export interface ModalProps {
  /** Controls visibility. The modal does not render any DOM when `false`. */
  isOpen: boolean;
  /** Render the dimmed backdrop behind the dialog. Defaults to `true`. */
  withBackdrop?: boolean;
  /** Render the built-in close (×) button in the header. Defaults to `true`. */
  withClose?: boolean;
  /** When true, clicking the backdrop calls `onClose`. Defaults to `true`. */
  outsideClickClose?: boolean;
  /** Subtitle / supporting text shown under `title` in the header. */
  description?: ReactNode;
  /** Heading text rendered at the top of the dialog. */
  title?: ReactNode;
  /**
   * Called for any close intent: the close button, backdrop click (when
   * `outsideClickClose`), and the Escape key. Also called when Enter is
   * pressed if `disableEnterClose` is false and `primaryBtnProps.onClick`
   * is set — see `disableEnterClose`.
   */
  onClose?: () => void;
  /** Body content of the dialog, rendered between the header and footer buttons. */
  children?: ReactNode;
  /** Optional illustration/icon rendered above the title. */
  imageNode?: ReactNode;
  /**
   * Props for the optional cancel button (rendered with `variant="tertiary"`
   * and `size="large"`). Pass `onClick` to handle the click.
   */
  cancelBtnProps?: ButtonProps;
  /**
   * Props for the optional secondary action button
   * (rendered with `variant="secondary"` and `size="large"`).
   */
  secondaryBtnProps?: ButtonProps;
  /**
   * Props for the optional primary action button
   * (rendered with `variant="primary"` and `size="large"`). When set and
   * `disableEnterClose` is false, pressing Enter triggers its `onClick`.
   */
  primaryBtnProps?: ButtonProps;
  /** Optional content rendered on the left side of the footer alongside the action buttons. */
  footerLeftAction?: ReactNode;
  /** Extra class forwarded to the outer wrapper. */
  className?: string;
  /** Inline styles forwarded to the outer wrapper. */
  style?: React.CSSProperties;
  /** Test selector. Defaults to `'modal-container'` when omitted. */
  'data-testid'?: string;
  /** Render the modal into a portal at the document root. Defaults to `true`. Set false to render in place. */
  inPortal?: boolean;
  /** Disable the Enter-key shortcut that triggers `primaryBtnProps.onClick`. Defaults to `false`. */
  disableEnterClose?: boolean;
}

const ModalComponent = forwardRef<HTMLButtonElement, ModalProps>(
  (
    {
      withBackdrop = true,
      withClose = true,
      outsideClickClose = true,
      isOpen,
      title,
      onClose,
      description,
      children,
      cancelBtnProps,
      secondaryBtnProps,
      primaryBtnProps,
      footerLeftAction,
      imageNode,
      className,
      style,
      inPortal = true,
      disableEnterClose = false,
      ...rest
    }: ModalProps,
    ref
  ) => {
    const backdrop = useRef<HTMLDivElement>(null);

    useDisableScroll(isOpen);

    useEffect(() => {
      const { current } = backdrop;

      const keyHandler = (e: KeyboardEvent) => {
        if (e.key === keyboard.esc.key) {
          onClose?.();
        } else if (
          e.key === keyboard.enter.key &&
          !disableEnterClose &&
          primaryBtnProps?.onClick &&
          !primaryBtnProps.disabled
        ) {
          e.preventDefault();
          //@ts-ignore
          primaryBtnProps.onClick();
        }
      };

      const clickHandler = (e: MouseEvent) => {
        outsideClickClose && e.target === current && onClose?.();
      };

      if (current) {
        current.addEventListener('click', clickHandler);
        window.addEventListener('keyup', keyHandler);
      }

      if (isOpen && inPortal) {
        document.querySelector('#app')?.setAttribute('inert', 'true');
      }

      return () => {
        if (current) {
          current.removeEventListener('click', clickHandler);
        }

        document.querySelector('#app')?.removeAttribute('inert');
        window.removeEventListener('keyup', keyHandler);
      };
    }, [isOpen, onClose, primaryBtnProps]);

    const content = isOpen ? (
      <div
        role="dialog"
        aria-labelledby={title ? MODAL_TITLE_ID : undefined}
        data-testid={rest['data-testid'] ?? 'modal-container'}
        data-modal-container
        className={clsx(styles.modalContainer, {
          [styles.modalContainerFill]: !inPortal,
        })}
        id={MODAL_ID}
      >
        <div className={styles.header} data-modal-header>
          {(title || description || imageNode) && (
            <div className={styles.textContainer} data-modal-text-container>
              {imageNode && (
                <div className={styles.imageContainer} data-modal-image>
                  {imageNode}
                </div>
              )}
              {title && (
                <h4
                  id={MODAL_TITLE_ID}
                  className={styles.title}
                  data-testid="modal-title"
                  data-modal-title
                >
                  {title}
                </h4>
              )}
              {description && (
                <div className={styles.description} data-modal-description>
                  {description}
                </div>
              )}
            </div>
          )}
          {withClose && (
            <Button
              aria-label="Close"
              className={styles.closeButton}
              data-testid="modal-close-button"
              data-modal-close
              id={CLOSE_MODAL_ID}
              onClick={onClose}
              iconName="Close"
              variant="empty"
              size="small"
              ref={ref}
            />
          )}
        </div>
        {children}
        {[cancelBtnProps, secondaryBtnProps, primaryBtnProps].some(Boolean) && (
          <div
            className={clsx(styles.buttonsContainer, {
              [styles.buttonsContainerWithLeftAction]: !!footerLeftAction,
            })}
            data-modal-buttons
          >
            {footerLeftAction && (
              <div className={styles.leftActionsContainer}>
                {footerLeftAction}
              </div>
            )}
            <div className={styles.rightActionsContainer}>
              {cancelBtnProps && (
                <Button
                  data-testid="modal-cancel-button"
                  variant="tertiary"
                  size="large"
                  {...cancelBtnProps}
                />
              )}
              {secondaryBtnProps && (
                <Button
                  data-testid="modal-secondary-button"
                  variant="secondary"
                  size="large"
                  {...secondaryBtnProps}
                />
              )}
              {primaryBtnProps && (
                <Button
                  data-testid="modal-primary-button"
                  variant="primary"
                  size="large"
                  {...primaryBtnProps}
                />
              )}
            </div>
          </div>
        )}
      </div>
    ) : null;

    const modalContent = (
      <div
        className={clsx(styles.container, className)}
        style={style}
        data-modal
      >
        {withBackdrop && (
          <div
            data-testid="modal-backdrop"
            data-modal-backdrop
            ref={backdrop}
            className={clsx(styles.backdrop, {
              [styles.backdropActive]: isOpen,
            })}
          />
        )}
        {content}
      </div>
    );

    return (
      <Fragment>
        {isOpen && (inPortal ? <Portal>{modalContent}</Portal> : modalContent)}
      </Fragment>
    );
  }
);

/**
 * Centered dialog rendered into a portal (by default) over a dimmed
 * backdrop.
 *
 * Provides a header (image + title + description + close button), a
 * `children` body, and an optional footer with up to three styled
 * action buttons (`cancelBtnProps`, `secondaryBtnProps`,
 * `primaryBtnProps`). Closes on Escape, on backdrop click (when
 * `outsideClickClose`), or via `withClose`'s × button. Disables page
 * scroll while open and applies `inert` to the rest of the app for
 * accessibility.
 *
 * The forwarded `ref` points at the close button, useful for focus
 * management on open.
 */
export const Modal: ForwardRefExoticComponent<
  ModalProps & RefAttributes<HTMLButtonElement>
> = ModalComponent;
